/**
 * Account actions — CRUD, relationships, classrooms.
 *
 * Extracted from store.ts as part of T1 (store.ts opsplitsen).
 */

import type { SetFn, GetFn, AppStoreState } from './types'
import { removeDemoAccounts } from './helpers'
import {
  getActiveAccount,
  switchActiveAccount,
  updateAccountName,
  updateAccountEmail as updateAccountEmailHelper,
  updateTeacherDetails as updateTeacherDetailsHelper,
  updateLearnerLinkCode as updateLearnerLinkCodeHelper,
  updateLearnerDetails as updateLearnerDetailsHelper,
  touchLastActive as touchLastActiveHelper,
  deleteAccountFromState,
  completeOnboarding as completeOnboardingHelper,
  updateAccountPassword as updateAccountPasswordHelper,
  createAccountInState,
  generateAccountId,
  linkParentToLearner,
  linkTeacherToLearner,
  unlinkParentFromLearner as unlinkParentFromLearnerHelper,
  unlinkTeacherFromLearner as unlinkTeacherFromLearnerHelper,
  restoreArchivedLearner as restoreArchivedLearnerHelper,
  addClassroom as addClassroomHelper,
  updateClassroom as updateClassroomHelper,
  removeClassroom as removeClassroomHelper,
  addLearnerToClassroom as addLearnerToClassroomHelper,
  removeLearnerFromClassroom as removeLearnerFromClassroomHelper,
  joinClassroomByCode as joinClassroomByCodeHelper,
  regenerateJoinCode as regenerateJoinCodeHelper,
  transferClassroomOwnership as transferClassroomOwnershipHelper,
  joinClassroomAsCoTeacher as joinClassroomAsCoTeacherHelper,
  leaveClassroomAsCoTeacher as leaveClassroomAsCoTeacherHelper,
  generateUniqueLinkCode,
} from '../../accounts'
import { INITIAL_STATE, createResetState } from '../../config/initialState'
import { GROUPS } from '../../config/systemConfig'
import type { GroupId } from '../../config/systemConfig'
import { dataLayer } from '../../data/dataLayer'
import { getAutoStartingTier } from '../../mastery'
import { debug } from '../../utils/logger'

type AccountActions = Pick<
  AppStoreState,
  | 'updateProfileDisplayName'
  | 'updateAccountEmail'
  | 'updateAccountNameById'
  | 'updateTeacherDetails'
  | 'updateLearnerDetails'
  | 'createAccount'
  | 'deleteAccount'
  | 'switchAccount'
  | 'clearActiveAccount'
  | 'touchLastActive'
  | 'resetLearnerProgress'
  | 'completeOnboarding'
  | 'updatePassword'
  | 'linkParentToLearnerById'
  | 'linkTeacherToLearnerById'
  | 'unlinkParentFromLearner'
  | 'unlinkTeacherFromLearner'
  | 'restoreArchivedLearner'
  | 'setLearnerLinkCode'
  | 'addClassroom'
  | 'updateClassroom'
  | 'removeClassroom'
  | 'addLearnerToClassroom'
  | 'removeLearnerFromClassroom'
  | 'joinClassroomByCode'
  | 'regenerateJoinCode'
  | 'transferClassroomOwnership'
  | 'joinClassroomAsCoTeacher'
  | 'leaveClassroomAsCoTeacher'
  | 'createAndLinkStudent'
>

export function createAccountActions(set: SetFn, get: GetFn): AccountActions {
  return {
    updateProfileDisplayName: (newName) => {
      set((s) => {
        if (!s.multiAccountState) return s
        const active = getActiveAccount(s.multiAccountState.accounts)
        if (!active) return s
        return {
          multiAccountState: {
            ...s.multiAccountState,
            accounts: updateAccountName(
              s.multiAccountState.accounts,
              active.id,
              newName,
            ),
          },
        }
      })
    },

    updateAccountEmail: (accountId, email) => {
      set((s) => {
        if (!s.multiAccountState) return s
        return {
          multiAccountState: {
            ...s.multiAccountState,
            accounts: updateAccountEmailHelper(
              s.multiAccountState.accounts,
              accountId,
              email,
            ),
          },
        }
      })
    },

    updateAccountNameById: (accountId, name) => {
      set((s) => {
        if (!s.multiAccountState) return s
        return {
          multiAccountState: {
            ...s.multiAccountState,
            accounts: updateAccountName(
              s.multiAccountState.accounts,
              accountId,
              name,
            ),
          },
        }
      })
    },

    updateTeacherDetails: (teacherId, updates) => {
      set((s) => {
        if (!s.multiAccountState) return s
        return {
          multiAccountState: {
            ...s.multiAccountState,
            accounts: updateTeacherDetailsHelper(
              s.multiAccountState.accounts,
              teacherId,
              updates,
            ),
          },
        }
      })
    },

    updateLearnerDetails: (learnerId, updates) => {
      set((s) => {
        if (!s.multiAccountState) return s
        return {
          multiAccountState: {
            ...s.multiAccountState,
            accounts: updateLearnerDetailsHelper(
              s.multiAccountState.accounts,
              learnerId,
              updates,
            ),
          },
        }
      })
    },

    createAccount: (role, details, options) => {
      const newId = generateAccountId()
      set((s) => {
        if (!s.multiAccountState) return s
        const cleanedState = options?.cleanupDemo
          ? removeDemoAccounts(s.multiAccountState)
          : s.multiAccountState
        const { accounts: updatedAccounts } = createAccountInState(
          cleanedState.accounts,
          role,
          details,
          newId,
        )
        const newLearnerData =
          role === 'learner'
            ? {
                [newId]: {
                  ...INITIAL_STATE,
                  stats: { ...INITIAL_STATE.stats, name: details.displayName },
                },
              }
            : {}
        const shouldActivate =
          options?.activate || !cleanedState.accounts.activeAccountId
        const accountsWithActive = {
          ...updatedAccounts,
          activeAccountId: shouldActivate
            ? newId
            : cleanedState.accounts.activeAccountId,
        }
        return {
          multiAccountState: {
            ...cleanedState,
            accounts: accountsWithActive,
            learnerData: { ...cleanedState.learnerData, ...newLearnerData },
          },
        }
      })
      return newId
    },

    deleteAccount: (accountId) => {
      set((s) => {
        if (!s.multiAccountState) return s
        const account = s.multiAccountState.accounts.accountsById[accountId]
        const updatedAccounts = deleteAccountFromState(
          s.multiAccountState.accounts,
          accountId,
        )
        if (updatedAccounts === s.multiAccountState.accounts) return s
        const newLearnerData = { ...s.multiAccountState.learnerData }
        if (account?.role === 'learner') {
          delete newLearnerData[accountId]
        }
        return {
          multiAccountState: {
            ...s.multiAccountState,
            accounts: updatedAccounts,
            learnerData: newLearnerData,
          },
        }
      })
    },

    switchAccount: (accountId) => {
      let learnerSeedHint: { group: GroupId; placedTier: number } | null = null
      set((s) => {
        if (!s.multiAccountState) return s
        const newAccounts = switchActiveAccount(
          s.multiAccountState.accounts,
          accountId,
        )
        // Sync selectedGroup to the new learner's grade
        const account = newAccounts.accountsById[accountId]
        let groupUpdate: Partial<AppStoreState> = {}
        if (account?.role === 'learner' && 'grade' in account) {
          const grade = (account as { grade?: number }).grade
          if (grade && GROUPS.includes(grade as GroupId)) {
            groupUpdate = {
              selectedGroup: grade as GroupId,
              groupVersion: s.groupVersion + 1,
            }
            dataLayer.setGroup(grade as GroupId)
            // FG3 S7 — capture the seed parameters; we fire the actual
            // seed action AFTER the set() commits, so the learner-scoped
            // updateLearnerState() in seedInferredMastery sees the newly
            // active account. Idempotent: re-seeds on every switch are
            // free because existing inferredMastery entries are preserved.
            learnerSeedHint = {
              group: grade as GroupId,
              placedTier: getAutoStartingTier(grade as GroupId),
            }
          }
        }
        return {
          multiAccountState: {
            ...s.multiAccountState,
            accounts: newAccounts,
          },
          viewingLearnerId: null,
          ...groupUpdate,
        }
      })
      if (learnerSeedHint) {
        const { group, placedTier } = learnerSeedHint
        get().seedInferredMastery(group, placedTier)
      }
      debug(`🔄 Switched to account: ${accountId}`)
    },

    clearActiveAccount: () => {
      set((s) => {
        if (!s.multiAccountState) return s
        return {
          multiAccountState: {
            ...s.multiAccountState,
            accounts: {
              ...s.multiAccountState.accounts,
              activeAccountId: '',
            },
          },
          viewingLearnerId: null,
        }
      })
      debug('🔒 Active account cleared (logout)')
    },

    touchLastActive: () => {
      set((s) => {
        if (!s.multiAccountState) return s
        const active = getActiveAccount(s.multiAccountState.accounts)
        if (!active) return s
        const updatedAccounts = touchLastActiveHelper(
          s.multiAccountState.accounts,
          active.id,
        )
        if (updatedAccounts === s.multiAccountState.accounts) return s
        return {
          multiAccountState: {
            ...s.multiAccountState,
            accounts: updatedAccounts,
          },
        }
      })
    },

    resetLearnerProgress: (learnerId) => {
      set((s) => {
        if (!s.multiAccountState) return s
        const learnerData = s.multiAccountState.learnerData[learnerId]
        if (!learnerData) return s
        return {
          multiAccountState: {
            ...s.multiAccountState,
            learnerData: {
              ...s.multiAccountState.learnerData,
              [learnerId]: createResetState(learnerData.stats.name),
            },
          },
        }
      })
      debug(`🗑️ Reset progress for learner: ${learnerId}`)
    },

    completeOnboarding: (accountId, stepState) => {
      let learnerSeedHint: { group: GroupId; placedTier: number } | null = null
      set((s) => {
        if (!s.multiAccountState) return s
        const targetId =
          accountId || s.multiAccountState.accounts.activeAccountId
        if (!targetId) return s
        const nextAccounts = completeOnboardingHelper(
          s.multiAccountState.accounts,
          targetId,
          stepState,
        )
        // FG3 S7 — onboarding is the semantic "learner is now real"
        // moment. If the target is the active learner, capture seed
        // parameters and fire seedInferredMastery after the set()
        // commits. If the target is a different account (admin creating
        // a learner), the seed will happen the first time that learner
        // is switched into via switchAccount.
        const target = nextAccounts.accountsById[targetId]
        const isActive = nextAccounts.activeAccountId === targetId
        if (isActive && target?.role === 'learner' && 'grade' in target) {
          const grade = (target as { grade?: number }).grade
          if (grade && GROUPS.includes(grade as GroupId)) {
            learnerSeedHint = {
              group: grade as GroupId,
              placedTier: getAutoStartingTier(grade as GroupId),
            }
          }
        }
        return {
          multiAccountState: {
            ...s.multiAccountState,
            accounts: nextAccounts,
          },
        }
      })
      if (learnerSeedHint) {
        const { group, placedTier } = learnerSeedHint
        get().seedInferredMastery(group, placedTier)
      }
      debug(`✅ Onboarding completed for account: ${accountId || 'active'}`)
    },

    updatePassword: (accountId, newPasswordHash) => {
      set((s) => {
        if (!s.multiAccountState) return s
        return {
          multiAccountState: {
            ...s.multiAccountState,
            accounts: updateAccountPasswordHelper(
              s.multiAccountState.accounts,
              accountId,
              newPasswordHash,
            ),
          },
        }
      })
      debug(`🔑 Password updated for account: ${accountId}`)
    },

    // ── Relationship Actions ──
    linkParentToLearnerById: (parentId, learnerId) => {
      set((s) => {
        if (!s.multiAccountState) return s
        return {
          multiAccountState: {
            ...s.multiAccountState,
            accounts: linkParentToLearner(
              s.multiAccountState.accounts,
              parentId,
              learnerId,
            ),
          },
        }
      })
    },

    linkTeacherToLearnerById: (teacherId, learnerId) => {
      set((s) => {
        if (!s.multiAccountState) return s
        return {
          multiAccountState: {
            ...s.multiAccountState,
            accounts: linkTeacherToLearner(
              s.multiAccountState.accounts,
              teacherId,
              learnerId,
            ),
          },
        }
      })
    },

    unlinkParentFromLearner: (parentId, learnerId) => {
      set((s) => {
        if (!s.multiAccountState) return s
        return {
          multiAccountState: {
            ...s.multiAccountState,
            accounts: unlinkParentFromLearnerHelper(
              s.multiAccountState.accounts,
              parentId,
              learnerId,
            ),
          },
        }
      })
    },

    unlinkTeacherFromLearner: (teacherId, learnerId) => {
      set((s) => {
        if (!s.multiAccountState) return s
        return {
          multiAccountState: {
            ...s.multiAccountState,
            accounts: unlinkTeacherFromLearnerHelper(
              s.multiAccountState.accounts,
              teacherId,
              learnerId,
            ),
          },
        }
      })
    },

    restoreArchivedLearner: (teacherId, learnerId, options) => {
      set((s) => {
        if (!s.multiAccountState) return s
        return {
          multiAccountState: {
            ...s.multiAccountState,
            accounts: restoreArchivedLearnerHelper(
              s.multiAccountState.accounts,
              teacherId,
              learnerId,
              options,
            ),
          },
        }
      })
      debug(`♻️ Restored archived learner: ${learnerId}`)
    },

    setLearnerLinkCode: (learnerId, linkCode) => {
      set((s) => {
        if (!s.multiAccountState) return s
        return {
          multiAccountState: {
            ...s.multiAccountState,
            accounts: updateLearnerLinkCodeHelper(
              s.multiAccountState.accounts,
              learnerId,
              linkCode,
            ),
          },
        }
      })
      debug(`🔗 Link code set for learner: ${learnerId} → ${linkCode}`)
    },

    // ── Classroom Actions ──
    addClassroom: (teacherId, classroom) => {
      set((s) => {
        if (!s.multiAccountState) return s
        return {
          multiAccountState: {
            ...s.multiAccountState,
            accounts: addClassroomHelper(
              s.multiAccountState.accounts,
              teacherId,
              classroom,
            ),
          },
        }
      })
    },

    updateClassroom: (teacherId, classroomId, updates) => {
      set((s) => {
        if (!s.multiAccountState) return s
        return {
          multiAccountState: {
            ...s.multiAccountState,
            accounts: updateClassroomHelper(
              s.multiAccountState.accounts,
              teacherId,
              classroomId,
              updates,
            ),
          },
        }
      })
    },

    removeClassroom: (teacherId, classroomId) => {
      set((s) => {
        if (!s.multiAccountState) return s
        return {
          multiAccountState: {
            ...s.multiAccountState,
            accounts: removeClassroomHelper(
              s.multiAccountState.accounts,
              teacherId,
              classroomId,
            ),
          },
        }
      })
    },

    addLearnerToClassroom: (teacherId, classroomId, learnerId) => {
      set((s) => {
        if (!s.multiAccountState) return s
        return {
          multiAccountState: {
            ...s.multiAccountState,
            accounts: addLearnerToClassroomHelper(
              s.multiAccountState.accounts,
              teacherId,
              classroomId,
              learnerId,
            ),
          },
        }
      })
    },

    removeLearnerFromClassroom: (teacherId, classroomId, learnerId) => {
      set((s) => {
        if (!s.multiAccountState) return s
        return {
          multiAccountState: {
            ...s.multiAccountState,
            accounts: removeLearnerFromClassroomHelper(
              s.multiAccountState.accounts,
              teacherId,
              classroomId,
              learnerId,
            ),
          },
        }
      })
    },

    joinClassroomByCode: (learnerId, joinCode) => {
      let result: { classroomId: string; teacherId: string } | null = null
      set((s) => {
        if (!s.multiAccountState) return s
        const joined = joinClassroomByCodeHelper(
          s.multiAccountState.accounts,
          learnerId,
          joinCode,
        )
        if (!joined) return s
        result = {
          classroomId: joined.classroomId,
          teacherId: joined.teacherId,
        }
        return {
          multiAccountState: {
            ...s.multiAccountState,
            accounts: joined.state,
          },
        }
      })
      if (result) {
        debug(
          `Learner ${learnerId} joined classroom ${(result as { classroomId: string }).classroomId} via joinCode`,
        )
      }
      return result
    },

    regenerateJoinCode: (teacherId, classroomId) => {
      let newCode: string | null = null
      set((s) => {
        if (!s.multiAccountState) return s
        const regenerated = regenerateJoinCodeHelper(
          s.multiAccountState.accounts,
          teacherId,
          classroomId,
        )
        newCode = regenerated.newCode
        return {
          multiAccountState: {
            ...s.multiAccountState,
            accounts: regenerated.state,
          },
        }
      })
      if (newCode) {
        debug(`Regenerated joinCode for classroom ${classroomId}: ${newCode}`)
      }
      return newCode
    },

    transferClassroomOwnership: (classroomId, newTeacherId) => {
      set((s) => {
        if (!s.multiAccountState) return s
        return {
          multiAccountState: {
            ...s.multiAccountState,
            accounts: transferClassroomOwnershipHelper(
              s.multiAccountState.accounts,
              classroomId,
              newTeacherId,
            ),
          },
        }
      })
      debug(`Transferred classroom ${classroomId} to teacher ${newTeacherId}`)
    },

    joinClassroomAsCoTeacher: (teacherId, classroomId) => {
      set((s) => {
        if (!s.multiAccountState) return s
        return {
          multiAccountState: {
            ...s.multiAccountState,
            accounts: joinClassroomAsCoTeacherHelper(
              s.multiAccountState.accounts,
              teacherId,
              classroomId,
            ),
          },
        }
      })
      debug(
        `Teacher ${teacherId} joined classroom ${classroomId} as co-teacher`,
      )
    },

    leaveClassroomAsCoTeacher: (teacherId, classroomId) => {
      set((s) => {
        if (!s.multiAccountState) return s
        return {
          multiAccountState: {
            ...s.multiAccountState,
            accounts: leaveClassroomAsCoTeacherHelper(
              s.multiAccountState.accounts,
              teacherId,
              classroomId,
            ),
          },
        }
      })
      debug(`Teacher ${teacherId} left classroom ${classroomId} as co-teacher`)
    },

    createAndLinkStudent: (teacherId, details, classroomId) => {
      const newId = generateAccountId()
      let generatedCode = ''
      set((s) => {
        if (!s.multiAccountState) return s
        const { accounts: withNewAccount } = createAccountInState(
          s.multiAccountState.accounts,
          'learner',
          { displayName: details.displayName, grade: details.grade },
          newId,
        )
        let withLink = linkTeacherToLearner(withNewAccount, teacherId, newId)
        if (classroomId) {
          withLink = addLearnerToClassroomHelper(
            withLink,
            teacherId,
            classroomId,
            newId,
          )
        }
        const code = generateUniqueLinkCode({
          ...s.multiAccountState.accounts,
          accountsById: withLink.accountsById,
        })
        generatedCode = code
        const learner = withLink.accountsById[newId]
        const withLinkCode = {
          ...withLink,
          accountsById: {
            ...withLink.accountsById,
            [newId]: { ...learner, linkCode: code },
          },
        }
        const newLearnerData = {
          ...INITIAL_STATE,
          stats: { ...INITIAL_STATE.stats, name: details.displayName },
        }
        return {
          multiAccountState: {
            ...s.multiAccountState,
            accounts: {
              ...withLinkCode,
              activeAccountId: s.multiAccountState.accounts.activeAccountId,
            },
            learnerData: {
              ...s.multiAccountState.learnerData,
              [newId]: newLearnerData,
            },
          },
        }
      })
      return { studentId: newId, linkCode: generatedCode }
    },
  }
}
